import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import JsonLd, { breadcrumbSchema } from '../components/JsonLd';
import ClientLogos from '../components/ClientLogos';
import Testimonials from '../components/Testimonials';
import { useScrollReveal } from '../hooks/useScrollReveal';

const reasons = [
  'We are a privately owned company based in the UK, who own their own code and have been trading for over 40 years. We are dedicated to supplying MIS systems to the printing and packaging industry. We supply some of the largest printers in the UK.',
  'We are technically led — everyone you have contact with will have worked in either the print or IT industries prior to joining us.',
  "We don't employ outside salespeople to sell our products and prefer to let our technical knowledge come to the fore. We pride ourselves on having the same people in contact with you from initial meetings right through to implementation of the project. For example, you will always be able to talk to the same people if you need to discuss progress or functionality.",
  'Our installation process starts with assistance and training on how to configure the system. This includes the use of pre-set value fields, such as default settings for VAT and customer mark ups. We would do this in close consultation with you and your team to create the optimum set-up for your needs. This is the key to our installation process and we have found it is much more efficient than the "Out of the box" approach.',
  'We can import all your customer information into the system, including: prospects, customers, clients, suppliers and stock levels, from external sources, to help you transition as quickly as possible.',
  "We don't penalise customers in regards to the number of licensed users. We examine your business and quote you upfront for the system you need and timetable the number of training days required.",
  'Finally, we offer financial support to help spread the upfront costs, (details can be supplied upon request).',
];

const ReasonsList = () => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section ref={ref} className="py-14 lg:py-20 bg-white" data-testid="about-reasons-section">
      <div className="section-container">
        <div className="max-w-4xl">
          <p
            className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            All About Imprint
          </p>
          <h2
            className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Reasons for you to consider giving Imprint a call about your{' '}
            <span className="text-[#00BCE4]">MIS project...</span>
          </h2>
          <ul className="space-y-5">
            {reasons.map((reason, index) => (
              <li
                key={`reason-${index}`}
                data-testid={`about-reason-${index}`}
                className={`flex items-start gap-4 transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <CheckCircle2 size={22} className="text-[#00BCE4] flex-shrink-0 mt-0.5" />
                <p
                  className="text-base text-gray-600 leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {reason}
                </p>
              </li>
            ))}
          </ul>
          <p
            className="mt-8 text-lg font-semibold text-gray-900"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Don&apos;t just take our word for it, see what our clients say about Imprint below!
          </p>
        </div>
      </div>
    </section>
  );
};

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="pt-0 lg:pt-28 pb-0 bg-gray-50 min-h-screen">
      <SEO
        title="About Us"
        description="UK-based MIS experts with 40+ years in print & packaging, offering tailored systems and hands-on technical support."
        url="https://imprint-mis.co.uk/about"
      />
      <JsonLd data={breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
      ])} />

      {/* Hero */}
      <section className="bg-white border-b border-gray-100 py-12 lg:py-16">
        <div className="section-container">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#00BCE4] transition-colors mb-6"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <p
            className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Who We Are
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="about-page-heading"
          >
            About <span className="text-[#00BCE4]">Imprint</span>
          </h1>
          <p
            className="text-lg text-gray-600 leading-relaxed max-w-3xl"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            UK-based MIS experts with over 40 years in print &amp; packaging,
            offering tailored systems and hands-on technical support.
          </p>
        </div>
      </section>

      <ReasonsList />

      <ClientLogos />
      <Testimonials />

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="section-container text-center">
          <h2
            className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to talk about your <span className="text-[#00BCE4]">MIS project?</span>
          </h2>
          <Link to="/#contact" data-testid="about-contact-cta" className="btn-primary text-xs">
            Get in Touch
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
